import { sessionService } from '@/providers/services/auth/session';

/**
 * Find the route path last occurrence
 * @return {String}
 */
export function getRoutePathLastOccurrence() {
  const currentLocationPath = window.location.pathname;
  return currentLocationPath.substr(currentLocationPath.lastIndexOf('/') + 1);
}

/**
 * Find the route path first occurrence
 * @return {String}
 */
export function getRoutePathFirstOccurrence() {
  const currentLocationPath = window.location.pathname;
  return currentLocationPath.split('/')[2];
}

/**
 * This Method is responsible for sorting the array object by asc or desc.
 * @param  {Array}
 * @param  {PropertyName}
 * @param  {string} 'ASC|DESC'
 */
export function sortByProperty<T>(array: T[], propName: keyof T, order: 'ASC' | 'DESC'): void {
  array.sort((a, b) => {
    if (a[propName] < b[propName]) {
      return -1;
    }

    if (a[propName] > b[propName]) {
      return 1;
    }
    return 0;
  });

  if (order === 'DESC') {
    array.reverse();
  }
}

export function sortByArray(arrayItem: any, sortItem: any) {
  arrayItem.sort((a: any, b: any) => {
    if (sortItem.indexOf(a) === -1) {
      return 1;
    }
    if (sortItem.indexOf(b) === -1) {
      return -1;
    }
    if (sortItem.indexOf(a) === sortItem.indexOf(b)) {
      return 0;
    }
    return sortItem.indexOf(a) < sortItem.indexOf(b) ? -1 : 1;

  });
}



export function getSum(dataList: any, property: any) {
  return dataList.reduce(
    (count: any, data: any) => count + data[property], 0);
}

export function getDomainId(id: any) {
  const ids = id.split('-');
  return `${ids[0]}-${ids[1]}-${ids[2]}`;
}
export function getCourseId(id: any) {
  const ids = id.split('-');
  return `${ids[0]}-${ids[1]}`;
}

export function getSubjectId(id: any) {
  return id.substring(0, id.indexOf('-'));
}

export function isMicroStandardId(id: any) {
  return /.*\d{2}-\d{2}/.test(id) || /.*\.\d{2}\.\d{2}\./.test(id);
}

export function getTaxonomyTags(
  taxonomy = [],
  editable = false,
  removable = false,
  canAdd = false,
) {
  return taxonomy.map((tagData) => {
    return {
      isActive: false,
      isReadonly: !editable,
      isRemovable: removable,
      canAdd,
      data: tagData,
    };
  });
}

export function resetList(list: any, currentItem: any, params = ['checked', 'id']) {
  list.map((item: any) => {
    if (item[params[0]] && currentItem[params[1]] !== item[params[1]]) {
      item[params[0]] = false;
    }
    return item;
  });
}

export function clone(obj: any) {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Get the resource format to be App compliant
 * @function
 * @param format
 * @returns {string}
 */
export function getResourceFormat(format: any) {
  return format ? format.split('_')[0] : undefined; // i.e video_resource to video
}

/**
 * Truncate given string into 72 chars
 * @function
 * @param text
 * @returns {string}
 */
export function truncateString(text: any, length = 95) {
  if (text && text.length > 100) {
    return `${text.substring(0, length)}...`;
  }
  return text;
}

export function copy(value: any) {
  return JSON.parse(JSON.stringify(value));
}

export function find(items: any, key: string) {
  return items.find(
    (activeItem: any) => activeItem![key]);
}

export const activeTab = (menus: any, key: string) => {
  return menus.find((library: any) => library.key === key);
};

export const generateColor = () => {
  return '#' + (Math.random().toString(16).slice(-6));
};


export function findAnyCompetencyTitle(standards: any) {
  standards = standards ? standards : [];
  const activeStandard = standards.find((standard: any) => standard.title);
  if (activeStandard) {
    return activeStandard.title;
  }
  return null;
}

export function gutCourseNames(courses: any) {
  courses = courses ? courses : [];
  return courses.slice(Math.floor(courses.length / 2), courses.length);
}

/**
 * Validating URL has protocol
 * @param url
 */
export function validateUrl(url: string = '') {
  if (url && !url.match(/^[a-zA-Z]+:\/\//)) {
    return 'http://' + url;
  }
  return url;
}

export function isEmptyValue(value: any) {
  if (
    value === '' ||
    value === undefined ||
    value === 0 ||
    value === null ||
    (typeof value === 'number' && isNaN(value))
  ) {
    return true;
  } else {
    return false;
  }
}

export function isEmptyObject(obj: any) {
  for (const prop in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, prop)) {
      return false;
    }
  }

  return JSON.stringify(obj) === JSON.stringify({});
}

export function resetListFrameWorkList(list: any, currentItem: any, params = ['checked', 'standard_framework_id']) {
  list.map((item: any) => {
    if (item[params[0]] && currentItem[params[1]] !== item[params[1]]) {
      item[params[0]] = false;
    }
    return item;
  });
}

export function checkCDNUrl(url: string = '') {
  const cdnUrls = sessionService.getCdnUrl();
  const contentCdnURL = cdnUrls.content_cdn_url;
  if (url) {
    if (url.indexOf('//cdn.') >= 0 || url.indexOf('//nile-') >= 0) {
      return url;
    } else {
      return contentCdnURL + url;
    }
  } else {
    return url;
  }
}

export function getShortNumber(value: number, args?: any): any {
  if (isNaN(value)) { return null; } // will only work value is a number
  if (value === null) { return null; }
  if (value === 0) { return null; }
  let abs = Math.abs(value);
  const rounder = Math.pow(10, 1);
  const isNegative = value < 0; // will also work for Negetive numbers
  let key = '';

  const powers = [
    { key: 'Q', value: Math.pow(10, 15) },
    { key: 'T', value: Math.pow(10, 12) },
    { key: 'B', value: Math.pow(10, 9) },
    { key: 'M', value: Math.pow(10, 6) },
    { key: 'K', value: 1000 },
  ];
  for (const power of powers) {
    let reduced = abs / power.value;
    reduced = Math.round(reduced * rounder) / rounder;
    if (reduced >= 1) {
      abs = reduced;
      key = power.key;
      break;
    }
  }
  return (isNegative ? '-' : '') + abs + key;
}

export function isAPICatchError(data: any) {
  return data.message === 'error' ? true : false;
}


